const GEOLOCATION_ENDPOINT = "/browsing_context_suggestions.json";
const MARKET_SWITCHER_PREFERENCE_KEY = "lyleMarketSwitcherPreference";
const LEGACY_PERSISTENCE_KEY = "cnvs";
const ONETRUST_WAIT_TIMEOUT_MS = 3000;
const CURRENT_COUNTRY_PLACEHOLDER = "__CURRENT_COUNTRY__";
const SUGGESTED_COUNTRY_PLACEHOLDER = "__SUGGESTED_COUNTRY__";

class LyleMarketSwitcher extends HTMLElement {
  #countries = [];
  #form = null;
  #modal = null;
  #modalCountryInput = null;
  #suggestedCountry = null;
  #suggestionPopup = null;
  #continueButton = null;
  #suggestedButton = null;
  #oneTrustTimer = null;
  #suggestionCheckStarted = false;

  connectedCallback() {
    this.#oneTrustTimer = null;
    this.#suggestionCheckStarted = false;
    this.#form = this.querySelector("form");
    this.#modal = this.querySelector("[data-market-switcher-modal]");
    this.#modalCountryInput = this.querySelector(
      "[data-market-switcher-modal-country]",
    );
    this.#suggestionPopup = this.querySelector(
      "#MarketSwitcherSuggestionPopup",
    );
    this.#continueButton = this.querySelector(
      ".market-switcher-modal__continue",
    );
    this.#suggestedButton = this.querySelector(
      ".market-switcher-modal__suggested",
    );
    this.#countries = this.#parseCountries();

    this.addEventListener("change", this.#handleChange);
    this.querySelector("[data-market-switcher-search]")?.addEventListener(
      "input",
      this.#handleSearch,
    );
    this.#continueButton?.addEventListener("click", this.#handleContinue);
    this.#suggestedButton?.addEventListener(
      "click",
      this.#handleSuggestedCountry,
    );
    window.addEventListener("onetrust:opened", this.#handleOneTrustOpened);
    window.addEventListener("onetrust:closed", this.#handleOneTrustClosed);

    this.#waitForOneTrust();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.#handleChange);
    this.querySelector("[data-market-switcher-search]")?.removeEventListener(
      "input",
      this.#handleSearch,
    );
    this.#continueButton?.removeEventListener("click", this.#handleContinue);
    this.#suggestedButton?.removeEventListener(
      "click",
      this.#handleSuggestedCountry,
    );
    window.removeEventListener("onetrust:opened", this.#handleOneTrustOpened);
    window.removeEventListener("onetrust:closed", this.#handleOneTrustClosed);

    if (this.#oneTrustTimer) {
      window.clearTimeout(this.#oneTrustTimer);
    }
  }

  #waitForOneTrust() {
    if (window.lyleOneTrustBannerOpen === true) {
      return;
    }

    if (window.lyleOneTrustBannerOpen === false) {
      this.#scheduleSuggestionCheck();
      return;
    }

    this.#oneTrustTimer = window.setTimeout(
      () => this.#scheduleSuggestionCheck(),
      ONETRUST_WAIT_TIMEOUT_MS,
    );
  }

  #scheduleSuggestionCheck() {
    if (this.#suggestionCheckStarted) return;

    this.#suggestionCheckStarted = true;

    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => this.#handleGeolocationSuggestion());
      return;
    }

    window.setTimeout(() => this.#handleGeolocationSuggestion(), 0);
  }

  #handleOneTrustOpened = () => {
    if (this.#oneTrustTimer) {
      window.clearTimeout(this.#oneTrustTimer);
    }

    this.#suggestionPopup?.hidePopover?.();
  };

  #handleOneTrustClosed = () => {
    if (this.#oneTrustTimer) {
      window.clearTimeout(this.#oneTrustTimer);
    }

    if (this.#suggestedCountry && !this.#getPreference()?.code) {
      this.#suggestionPopup?.showPopover?.();
      return;
    }

    this.#scheduleSuggestionCheck();
  };

  #parseCountries() {
    const source = this.querySelector("[data-market-switcher-countries]");

    if (!source?.textContent) {
      return [];
    }

    try {
      const countries = JSON.parse(source.textContent);
      return Array.isArray(countries) ? countries : [];
    } catch {
      return [];
    }
  }

  #handleChange = (event) => {
    const input = event.target.closest("[data-market-switcher-submit]");

    if (!input) {
      return;
    }

    if (input.dataset.marketSwitcherType === "country") {
      this.#setPreference({
        code: input.value,
        name: input.dataset.marketSwitcherName || input.value,
      });
    }

    input.closest("[popover]")?.hidePopover?.();
    this.#submitForm();
  };

  #handleSearch = (event) => {
    const query = event.target.value.trim().toLowerCase();
    const items = [...this.querySelectorAll("[data-market-switcher-country]")];
    let visibleCount = 0;

    items.forEach((item) => {
      const searchValue = item.dataset.marketSwitcherSearchValue || "";
      const isVisible = !query || searchValue.includes(query);

      item.hidden = !isVisible;

      if (isVisible) {
        visibleCount += 1;
      }
    });

    const emptyState = this.querySelector("[data-market-switcher-empty]");

    if (emptyState) {
      emptyState.hidden = visibleCount > 0;
    }
  };

  #handleContinue = () => {
    this.#setPreference({
      code: this.dataset.currentCountryCode,
      name: this.dataset.currentCountryName,
    });
    this.#suggestionPopup?.hidePopover?.();
  };

  #handleSuggestedCountry = () => {
    if (!this.#suggestedCountry) {
      return;
    }

    this.#setPreference(this.#suggestedCountry);

    const countryInput = [
      ...this.querySelectorAll('input[name="country_code"]'),
    ].find((input) => {
      return input.value === this.#suggestedCountry.code;
    });

    if (countryInput) {
      countryInput.checked = true;
    } else if (this.#modalCountryInput) {
      this.#modalCountryInput.disabled = false;
      this.#modalCountryInput.value = this.#suggestedCountry.code;
    }

    this.#submitForm();
  };

  async #handleGeolocationSuggestion() {
    if (this.dataset.enableModal !== "true") {
      return;
    }

    if (this.#getPreference()?.code) {
      return;
    }

    const geolocation = await this.#fetchGeolocation();
    const detectedCode =
      geolocation?.detected_values?.country?.handle?.toUpperCase();

    if (!detectedCode) {
      return;
    }

    const suggestedCountry =
      this.#countries.find((country) => country.code === detectedCode) ||
      this.#countries.find((country) => country.default) ||
      null;

    if (!suggestedCountry) {
      return;
    }

    if (suggestedCountry.code === this.dataset.currentCountryCode) {
      return;
    }

    this.#suggestedCountry = suggestedCountry;
    this.#updateModalContent(suggestedCountry);

    if (window.lyleOneTrustBannerOpen !== true) {
      this.#suggestionPopup?.showPopover?.();
    }
  }

  async #fetchGeolocation() {
    try {
      const response = await fetch(GEOLOCATION_ENDPOINT, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        return null;
      }

      return response.json();
    } catch {
      return null;
    }
  }

  #updateModalContent(suggestedCountry) {
    if (!this.#modal) {
      return;
    }

    const replacements = {
      [CURRENT_COUNTRY_PLACEHOLDER]: this.dataset.currentCountryName || "",
      [SUGGESTED_COUNTRY_PLACEHOLDER]:
        suggestedCountry.name || suggestedCountry.code,
    };

    this.#setText(
      "[data-market-switcher-modal-title]",
      this.#modal.dataset.titleTemplate,
      replacements,
    );
    this.#setText(
      "[data-market-switcher-modal-text]",
      this.#modal.dataset.textTemplate,
      replacements,
    );
    this.#setText(
      ".market-switcher-modal__suggested .button__label",
      this.#modal.dataset.suggestedCtaTemplate,
      replacements,
    );
    this.#setText(
      ".market-switcher-modal__continue .button__label",
      this.#modal.dataset.continueCtaTemplate,
      replacements,
    );
  }

  #setText(selector, template, replacements) {
    const element = this.querySelector(selector);

    if (!element || !template) {
      return;
    }

    let text = template;

    Object.entries(replacements).forEach(([placeholder, value]) => {
      text = text.replaceAll(placeholder, value);
    });

    element.textContent = text;
  }

  #setPreference(country) {
    if (!country?.code) {
      return;
    }

    try {
      window.localStorage.setItem(
        MARKET_SWITCHER_PREFERENCE_KEY,
        JSON.stringify({
          code: country.code,
          name: country.name || country.code,
        }),
      );
    } catch {
      // Storage can be unavailable in private browsing or strict privacy modes.
    }
  }

  #getPreference() {
    try {
      const preference = window.localStorage.getItem(
        MARKET_SWITCHER_PREFERENCE_KEY,
      );

      if (preference) {
        return JSON.parse(preference);
      }

      const legacyState = JSON.parse(
        window.localStorage.getItem(LEGACY_PERSISTENCE_KEY) || "{}",
      );
      return legacyState?.["market-switcher"]?.preference || null;
    } catch {
      return null;
    }
  }

  #submitForm() {
    this.#form?.submit();
  }
}

if (!customElements.get("lyle-market-switcher")) {
  customElements.define("lyle-market-switcher", LyleMarketSwitcher);
}
